import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { DescribeProfile, type DescribeIndicator } from './-components/orgs/describe-profile'
import { useEffect, useState } from 'react'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { Animate } from './-components/animate'
import { toast } from '@/lib/toast'
import { trpc } from '@/main'
import { useLingui } from '@lingui/react/macro'
import { useIndicators } from '@/hooks/use-indicators'
import { Loader2Icon } from 'lucide-react'
import type { IndicatorIndexes } from 'shared'
import { CenteredSpinnerPending } from './-components/app-layout-skeleton'
import { useClientOnlyQuery } from '@/hooks/use-client-only-query'

export const Route = createFileRoute('/_app/orgs_/$orgId/users/groups/$groupId/describe/$indicator/$step')({
  loader: ({ context, params }) => {
    context.queryClient.prefetchQuery(trpc.orgs.getOrgAndGroups.queryOptions(params.orgId))
  },
  component: RouteComponent,
  pendingComponent: CenteredSpinnerPending,
})

type Indicator = 'basic' | 'professional' | 'transversal' | 'directives' | 'finish'
type IndicatorWithoutFinish = Exclude<Indicator, 'finish'>
type Step = 'relevant' | 'irrelevant'
type Questionnaire = { relevant?: number[] | null; irrelevant?: number[] | null }

function seedFromQuestionnaire(questionnaire: Questionnaire | null | undefined): DescribeIndicator[] {
  return Array.from({ length: 18 }).map((_value, index) => ({
    index,
    selected: questionnaire?.relevant?.includes(index)
      ? ('relevant' as const)
      : questionnaire?.irrelevant?.includes(index)
        ? ('irrelevant' as const)
        : undefined,
  }))
}

function RouteComponent() {
  const { t } = useLingui()
  const navigate = useNavigate()

  const { orgId, groupId, indicator, step } = Route.useParams()

  const { data: org } = useSuspenseQuery(trpc.orgs.getOrgAndGroups.queryOptions(orgId))
  const { mutateAsync: validateGroupPassword } = useMutation(trpc.orgs.validateGroupPassword.mutationOptions())

  useEffect(() => {
    validateGroupPassword({ groupId, password: sessionStorage.getItem('password') ?? '' }).then((isAuthenticated) => {
      if (!isAuthenticated) {
        toast.error(t`You are not authorized`)
        navigate({ to: '/orgs/$orgId/users/groups/$groupId', params: { orgId, groupId } })
      }
    })
  }, [groupId, navigate, orgId, t, validateGroupPassword])

  const orgUserId = sessionStorage.getItem('orgUserId') ?? ''

  const group = org?.groups.find((g) => g.id === groupId)

  const indicatorWithoutFinish = (indicator === 'finish' ? 'transversal' : indicator) as IndicatorWithoutFinish

  // Keyed on `sessionStorage` (orgUserId + the group password), which does not exist on the server.
  const { data: questionnaire, isPending: isQuestionnairePending } = useClientOnlyQuery(
    trpc.orgs.getGroupUserQuestionnaire.queryOptions(
      {
        orgId,
        groupId,
        orgUserId,
        orgType: indicatorWithoutFinish,
        password: sessionStorage.getItem('password') ?? '',
      },
      { placeholderData: undefined },
    ),
  )

  if (!group) return null

  if (indicator === 'finish') {
    return (
      <section className="flex w-full max-w-5xl flex-col items-center gap-6 px-6 pb-6 text-center">
        {org && (
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-xl font-bold">{org.name}</h1>
            <h2 className="text-muted-foreground">{group.name}</h2>
          </div>
        )}
        <section className="flex flex-col items-center gap-6">
          <h2 className="text-xl font-bold">{t`Survey successfully completed.`}</h2>
          <p className="max-w-3xl text-center leading-loose whitespace-pre-wrap">{t`Thank you for your time and contribution!`}</p>
        </section>
      </section>
    )
  }

  return (
    <section className="flex w-full max-w-5xl flex-col items-center gap-6 px-6 pb-6 text-center">
      {org && (
        <div className="flex flex-col items-center gap-1">
          <h1 className="text-xl font-bold">{org.name}</h1>
          <h2 className="text-muted-foreground">{group.name}</h2>
        </div>
      )}

      {isQuestionnairePending ? (
        <Loader2Icon className="text-muted-foreground my-12 size-6 animate-spin" />
      ) : (
        <GroupDescribeStep
          key={indicatorWithoutFinish}
          orgId={orgId}
          groupId={groupId}
          group={group}
          indicator={indicatorWithoutFinish}
          step={step as Step}
          orgUserId={orgUserId}
          questionnaire={questionnaire}
        />
      )}
    </section>
  )
}

interface GroupDescribeStepProps {
  orgId: string
  groupId: string
  group: { basic?: boolean; professional?: boolean; transversal?: boolean; directives?: boolean }
  indicator: IndicatorWithoutFinish
  step: Step
  orgUserId: string
  /** Seeds the selection state ONCE at mount; the parent keys this on `indicator`. */
  questionnaire: Questionnaire | null | undefined
}

function GroupDescribeStep(props: GroupDescribeStepProps) {
  const { t } = useLingui()
  const { indicatorsOrgs } = useIndicators()
  const navigate = useNavigate()

  const [_indicators, setIndicators] = useState<DescribeIndicator[]>(() => seedFromQuestionnaire(props.questionnaire))

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [props.step])

  const indicators = _indicators.map((value) => ({
    index: value.index,
    selected: value.selected,
    name: indicatorsOrgs[props.indicator][value.index]?.name ?? '',
    description: indicatorsOrgs[props.indicator][value.index]?.description ?? '',
    color:
      props.indicator === 'basic'
        ? 'var(--color-radar-blue)'
        : props.indicator === 'professional'
          ? 'var(--color-radar-green)'
          : props.indicator === 'transversal'
            ? 'var(--color-radar-yellow)'
            : 'var(--color-radar-hippie-blue)',
  }))

  const { mutateAsync: putGroupUserQuestionnaire } = useMutation(trpc.orgs.putGroupUserQuestionnaire.mutationOptions())

  const putQuestionnaire = async (orgType: 'basic' | 'professional' | 'transversal' | 'directives') => {
    const relevant = indicators.filter((ind) => ind.selected === 'relevant').map((ind) => ind.index as IndicatorIndexes)
    const irrelevant = indicators
      .filter((ind) => ind.selected === 'irrelevant')
      .map((ind) => ind.index as IndicatorIndexes)

    const response = await putGroupUserQuestionnaire({
      groupId: props.groupId,
      orgId: props.orgId,
      // Password set in sessionStorage after validateGroupPassword succeeds earlier in this flow; the
      // server re-validates it in saveGroupUserQuestionnaire, so an empty/stale value is rejected there.
      password: sessionStorage.getItem('password') ?? '',
      orgUserId: props.orgUserId,
      orgType,
      questionnaire: {
        relevant: relevant.length === 0 ? undefined : relevant,
        irrelevant: irrelevant.length === 0 ? undefined : irrelevant,
      },
    })

    if ('error' in response) {
      toast.error(t`You are not authorized`)
      navigate({ to: '/orgs/$orgId/users/groups/$groupId', params: { orgId: props.orgId, groupId: props.groupId } })
    }
  }

  const steps = {
    ...(!props.group?.basic
      ? {}
      : {
          basic: {
            relevant: {
              title: (
                <span style={{ color: 'var(--color-radar-blue)' }}>{t`Relevant BASIC / ATTITUDINAL competencies`}</span>
              ),
              tooltip: t`These refer to the knowledge necessary to be able to act in relation to the labour market, such as the basic knowledge of how it works and the management of resources necessary to be able to access employment.\n\nIt could be said that they are the basic requirements to be able to search for, access and keep a job, although that job may have specific peculiarities.`,
              description: t`Select between three and six BASIC / ATTITUDINAL competencies that are relevant to your role.\nClick on a competency to select it, click again to discard it.\nIf you click on the circle, you will see the definition of each competency.`,
              step: 'relevant' as const,
              indicators,
              setIndicators,
              onNextStep: () => {
                putQuestionnaire('basic')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'basic', step: 'irrelevant' },
                })
              },
            },
            irrelevant: {
              title: (
                <span
                  style={{ color: 'var(--color-radar-blue)' }}
                >{t`Non-relevant BASIC / ATTITUDINAL competencies`}</span>
              ),
              tooltip: t`These refer to the knowledge necessary to be able to act in relation to the labour market, such as the basic knowledge of how it works and the management of resources necessary to be able to access employment.\n\nIt could be said that they are the basic requirements to be able to search for, access and keep a job, although that job may have specific peculiarities.`,
              description: t`Select between three and six BASIC / ATTITUDINAL competencies that are NOT relevant to your role.\nClick on a competency to select it, click again to deselect it.\nIf you click on the circle, you will see the definition of the competency`,
              step: 'irrelevant' as const,
              indicators,
              setIndicators,
              onPreviousStep: () => {
                putQuestionnaire('basic')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'basic', step: 'relevant' },
                })
              },
              onNextStep: () => {
                putQuestionnaire('basic')
                if (props.group.professional) {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'professional', step: 'relevant' },
                  })
                } else if (props.group.transversal) {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'transversal', step: 'relevant' },
                  })
                } else if (props.group.directives) {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'directives', step: 'relevant' },
                  })
                } else {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'finish', step: 'finish' },
                  })
                }
              },
            },
          },
        }),
    ...(!props.group?.professional
      ? {}
      : {
          professional: {
            relevant: {
              title: (
                <span
                  style={{ color: 'var(--color-radar-green)' }}
                >{t`Relevant PROFESSIONAL / FUNCTIONAL competencies`}</span>
              ),
              tooltip: t`Set of observable behaviors that result in excellent professional performance in a work environment and in a specific organization.\n\nThese behaviors are the fruit of the integrated functioning of: conceptual connections, mastery of procedures or techniques, abilities, attitudes, motivations and personality traits.`,
              description: t`Select between three and six PROFESSIONAL / FUNCTIONAL competencies that are relevant to your role.\nClick on a competency to select it, click again to discard it.\nIf you click on the circle, you will see the definition of each competency.`,
              step: 'relevant' as const,
              indicators,
              setIndicators,
              onPreviousStep: !props.group.basic
                ? undefined
                : () => {
                    putQuestionnaire('professional')
                    navigate({
                      to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                      params: { orgId: props.orgId, groupId: props.groupId, indicator: 'basic', step: 'irrelevant' },
                    })
                  },
              onNextStep: () => {
                putQuestionnaire('professional')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'professional', step: 'irrelevant' },
                })
              },
            },
            irrelevant: {
              title: (
                <span
                  style={{ color: 'var(--color-radar-green)' }}
                >{t`Non-relevant PROFESSIONAL / FUNCTIONAL competencies`}</span>
              ),
              tooltip: t`Set of observable behaviors that result in excellent professional performance in a work environment and in a specific organization.\n\nThese behaviors are the fruit of the integrated functioning of: conceptual connections, mastery of procedures or techniques, abilities, attitudes, motivations and personality traits.`,
              description: t`Select between three and six PROFESSIONAL / FUNCTIONAL competencies that are NOT relevant to your role.\nClick on a competency to select it, click again to deselect it.\nIf you click on the circle, you will see the definition of the competency`,
              step: 'irrelevant' as const,
              indicators,
              setIndicators,
              onPreviousStep: () => {
                putQuestionnaire('professional')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'professional', step: 'relevant' },
                })
              },
              onNextStep: () => {
                putQuestionnaire('professional')
                if (props.group.transversal) {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'transversal', step: 'relevant' },
                  })
                } else if (props.group.directives) {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'directives', step: 'relevant' },
                  })
                } else {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'finish', step: 'finish' },
                  })
                }
              },
            },
          },
        }),
    ...(!props.group?.transversal
      ? {}
      : {
          transversal: {
            relevant: {
              title: <span style={{ color: 'var(--color-radar-yellow)' }}>{t`Relevant TRANSVERSAL competencies`}</span>,
              tooltip: t`A set of ways of doing things and ways of being as professionals, as well as ways of relating to others, that are required for the adequate performance of the corresponding functions. They express "how the professional has to act" in the exercise of his or her profession. They are called "transversal" because they are competencies applicable to many different jobs.\n\nTransversal competencies are required to see how the professional acts in any job (functional and/or hierarchical) in the same organization.`,
              description: t`Select between three and six TRANSVERSAL competencies that are relevant to your role.\nClick on a competency to select it, click again to discard it.\nIf you click on the circle, you will see the definition of each competency.`,
              step: 'relevant' as const,
              indicators,
              setIndicators,
              onPreviousStep:
                !props.group.basic && !props.group.professional
                  ? undefined
                  : () => {
                      putQuestionnaire('transversal')

                      if (props.group.professional) {
                        navigate({
                          to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                          params: {
                            orgId: props.orgId,
                            groupId: props.groupId,
                            indicator: 'professional',
                            step: 'irrelevant',
                          },
                        })
                      } else if (props.group.basic) {
                        navigate({
                          to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                          params: {
                            orgId: props.orgId,
                            groupId: props.groupId,
                            indicator: 'basic',
                            step: 'irrelevant',
                          },
                        })
                      }
                    },
              onNextStep: () => {
                putQuestionnaire('transversal')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'transversal', step: 'irrelevant' },
                })
              },
            },
            irrelevant: {
              title: (
                <span style={{ color: 'var(--color-radar-yellow)' }}>{t`Non-relevant TRANSVERSAL competencies`}</span>
              ),
              tooltip: t`A set of ways of doing things and ways of being as professionals, as well as ways of relating to others, that are required for the adequate performance of the corresponding functions. They express "how the professional has to act" in the exercise of his or her profession. They are called "transversal" because they are competencies applicable to many different jobs.\n\nTransversal competencies are required to see how the professional acts in any job (functional and/or hierarchical) in the same organization.`,
              description: t`Select between three and six TRANSVERSAL competencies that are NOT relevant to your role.\nClick on a competency to select it, click again to deselect it.\nIf you click on the circle, you will see the definition of the competency`,
              step: 'irrelevant' as const,
              indicators,
              setIndicators,
              onPreviousStep: () => {
                putQuestionnaire('transversal')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'transversal', step: 'relevant' },
                })
              },
              onNextStep: () => {
                putQuestionnaire('transversal')
                if (props.group.directives) {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'directives', step: 'relevant' },
                  })
                } else {
                  navigate({
                    to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                    params: { orgId: props.orgId, groupId: props.groupId, indicator: 'finish', step: 'finish' },
                  })
                }
              },
            },
          },
        }),
    ...(!props.group?.directives
      ? {}
      : {
          directives: {
            relevant: {
              title: (
                <span style={{ color: 'var(--color-radar-hippie-blue)' }}>{t`Relevant MANAGERIAL competencies`}</span>
              ),
              tooltip: t`The set of knowledge, skills, abilities, and values that enable public leaders to manage teams and resources, make strategic decisions, and adapt to complex and changing environments to effectively and efficiently achieve the general interest of the institution and generate public value. These competencies are essential for driving modernization, strengthening governance, and proactively responding to user expectations.`,
              description: t`Select between three and six MANAGERIAL competencies that are relevant to your role.\nClick on a competency to select it, click again to discard it.\nIf you click on the circle, you will see the definition of each competency.`,
              step: 'relevant' as const,
              indicators,
              setIndicators,
              onPreviousStep:
                !props.group.basic && !props.group.professional && !props.group.transversal
                  ? undefined
                  : () => {
                      putQuestionnaire('directives')

                      if (props.group.transversal) {
                        navigate({
                          to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                          params: {
                            orgId: props.orgId,
                            groupId: props.groupId,
                            indicator: 'transversal',
                            step: 'irrelevant',
                          },
                        })
                      } else if (props.group.professional) {
                        navigate({
                          to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                          params: {
                            orgId: props.orgId,
                            groupId: props.groupId,
                            indicator: 'professional',
                            step: 'irrelevant',
                          },
                        })
                      } else if (props.group.basic) {
                        navigate({
                          to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                          params: {
                            orgId: props.orgId,
                            groupId: props.groupId,
                            indicator: 'basic',
                            step: 'irrelevant',
                          },
                        })
                      }
                    },
              onNextStep: () => {
                putQuestionnaire('directives')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'directives', step: 'irrelevant' },
                })
              },
            },
            irrelevant: {
              title: (
                <span style={{ color: 'var(--color-radar-hippie-blue)' }}>
                  {t`Non-relevant MANAGERIAL competencies`}
                </span>
              ),
              tooltip: t`The set of knowledge, skills, abilities, and values that enable public leaders to manage teams and resources, make strategic decisions, and adapt to complex and changing environments to effectively and efficiently achieve the general interest of the institution and generate public value. These competencies are essential for driving modernization, strengthening governance, and proactively responding to user expectations.`,
              description: t`Select between three and six MANAGERIAL competencies that are NOT relevant to your role.\nClick on a competency to select it, click again to deselect it.\nIf you click on the circle, you will see the definition of the competency`,
              step: 'irrelevant' as const,
              indicators,
              setIndicators,
              onPreviousStep: () => {
                putQuestionnaire('directives')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'directives', step: 'relevant' },
                })
              },
              onNextStep: () => {
                putQuestionnaire('directives')
                navigate({
                  to: '/orgs/$orgId/users/groups/$groupId/describe/$indicator/$step',
                  params: { orgId: props.orgId, groupId: props.groupId, indicator: 'finish', step: 'finish' },
                })
              },
            },
          },
        }),
  }

  const stepValue = props.indicator in steps ? steps[props.indicator] : undefined
  if (!stepValue) return null

  return (
    <Animate key={props.indicator} className="w-full">
      <DescribeProfile {...stepValue[props.step]} />
    </Animate>
  )
}
